import { computed, ref, watch} from "vue";
import { useApi, useToaster } from "@shopexperts/shared";
import type { AgencyType, AgencyTypeFields, AppTier, OverallReviewsSchema, ProfileExpertListingSchema, ProfileAgencyListingSchema, ProfileFreelancerListingSchema, PartnerBadge, ShopifyPartnerTier } from "@shopexperts/shared";

import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import type z from "zod";
import { getValidProfileURL } from "../utils/getValidProfileURL";


type ProfileDetails = Awaited<ReturnType<ReturnType<typeof useApi>["expert"]["getProfileDetails"]>>;
type ProfileExpert = z.infer<typeof ProfileExpertListingSchema>;
type ProfileAgency = z.infer<typeof ProfileAgencyListingSchema>;
type ProfileFreelancer = z.infer<typeof ProfileFreelancerListingSchema>;

type AgencyProfile = ProfileExpert & ProfileAgency & {
  expert_type: 'agency';
  partner_tier?: ShopifyPartnerTier | null;
  app_tier?: AppTier;
  agency_type?: AgencyType;
  agency_type_fields?: AgencyTypeFields;
  social_links: {
    linkedin_url: string;
    github_url: string;
    x_url: string;
    fiverr_url: string;
    upwork_url: string;
    toptal_url: string;
    storetasker_url: string;
    other_listing_url: string;
    website: string;
    shopify_directory_link: string;
    booking_call_url: string;
    google_merchant: string;
  };
  address: {
    address: string;
    latitude?: number | null;
    longitude?: number | null;
  };
};

type FreelancerProfile = ProfileExpert & ProfileFreelancer & {
  expert_type: 'freelancer';
  partner_tier?: ShopifyPartnerTier | null;
  social_links: {
    linkedin_url: string;
    github_url: string;
    x_url: string;
    fiverr_url: string;
    upwork_url: string;
    toptal_url: string;
    storetasker_url: string;
    other_listing_url: string;
    website: string;
    shopify_directory_link: string;
    booking_call_url: string;
    google_merchant: string;
  };
  address: {
    address: string;
    latitude?: number | null;
    longitude?: number | null;
  };
};

type ProfileUnion = AgencyProfile | FreelancerProfile;
type ProfileSelectedSubcategory = {
  id: number;
  name: string;
  slug: string;
  category: {
    id: number;
    name: string;
    slug: string;
  };
};

function getAppTierValue(
  agencyTypeFields: AgencyTypeFields | undefined,
): AppTier | undefined {
  const appTierField = agencyTypeFields?.app_tier;
  if (appTierField === undefined) return undefined;
  if (appTierField === null || typeof appTierField === "string") {
    return appTierField as AppTier;
  }
  if (typeof appTierField === "object" && "value" in appTierField) {
    return (appTierField.value as AppTier | undefined) ?? undefined;
  }
  return undefined;
}

function fix_faulty_api_array(array: string[]) {
  return array.map((item) => {
    const parsingRegex = /[A-z]{2}/;
    const iso2 = item.match(parsingRegex);

    if (iso2) {
      return iso2[0];
    }
    return "";
  });
}

function normalizeStringArray(array: unknown): string[] {
  if (!Array.isArray(array)) return [];
  return array.filter((item): item is string => typeof item === "string" && item.length > 0);
}

export function useProfileDetails() {
  const isLoadingProfile = ref(false);
  const profileError = ref<string | null>(null);
  const profile = ref<ProfileUnion | null>(null)
  const hasExpert = ref<boolean | null>(null);
  const offeredServices = ref<unknown[] | null>(null);
  const overallReviews = ref<z.infer<typeof OverallReviewsSchema> | null>(null);
  const badges = ref<PartnerBadge[]>([]);
  const toaster = useToaster();

  const getErrorMessage = (error: Error & { response?: { data?: { message?: string } } }) =>
    error?.message || error?.response?.data?.message || "Something went wrong. Please try again.";

  const mapToProfile = (data: ProfileDetails): ProfileUnion | null => {
    if (!data) return null;
    const expert = data.expert;
    if (!expert) return null;
    const common: ProfileExpert = {
      slug: expert.slug || "",
      bio: expert.bio || "",
      country: expert.country || "",
      city: expert.city ?? null,
      starting_price: expert.starting_price || 0,
      timezone: expert.timezone || "",
      phone_no: expert.phone_no || "",
      regions_served: Array.isArray(expert.regions_served) ? fix_faulty_api_array(expert.regions_served) : [],
      geo_regions: normalizeStringArray(expert.geo_regions),
      languages: Array.isArray(expert?.languages) ? fix_faulty_api_array(expert.languages) : [],
      profile_picture: expert && getValidProfileURL(expert.profile_picture || ""),
      profile_picture_media: expert.profile_picture_media || null,
      subcategories: ((expert as ProfileExpert & { subcategories?: ProfileSelectedSubcategory[] }).subcategories || []).map((subcategory) => ({
        ...subcategory,
        category: subcategory.category,
      })),
    };

    let expertProfile: ProfileUnion;

    if (data.user_type === "agency") {
      const agency = data.agency;
      if (!agency) return null;
      
      const representativePicture = data.agency?.representative_picture 
        ? getValidProfileURL(data.agency.representative_picture as string) 
        : undefined;
      const representativeName = data.agency?.representative_name as string | undefined;
      const representativeRole = data.agency?.representative_role as string | undefined;
      
      expertProfile = {
        ...common,
        expert_type: 'agency' as const,
        agency_name: agency.agency_name || "",
        agency_type: agency.agency_type ?? null,
        agency_type_fields: agency.agency_type_fields,
        app_tier: getAppTierValue(agency.agency_type_fields),
        representative_picture: representativePicture,
        representative_picture_media: data.agency?.representative_picture_media || null,
        representative_name: representativeName,
        representative_role: representativeRole,
        partner_tier: expert.partner_tier || null,
        social_links: {
          linkedin_url: data.social_links?.linkedin_url || "", 
          github_url: data.social_links?.github_url || "",
          x_url: data.social_links?.x_url || "",
          fiverr_url: data.social_links?.fiverr_url || "",
          upwork_url: data.social_links?.upwork_url || "",
          toptal_url: data.social_links?.toptal_url || "",
          storetasker_url: data.social_links?.storetasker_url || "",
          other_listing_url: data.social_links?.other_listing_url || "",
          website: data.social_links?.website || "",
          shopify_directory_link: data.social_links?.shopify_directory_link || "",
          booking_call_url: data.social_links?.booking_call_url || "",
          google_merchant: data.social_links?.google_merchant || "",
        },
        address: {
          address: data.address?.address || "",
          latitude: data.address?.latitude,
          longitude: data.address?.longitude,
        },
      };
      return expertProfile;
    }


    const freelancer = data?.freelancer!;
    expertProfile = {
      ...common,
      expert_type: 'freelancer',
      first_name: freelancer?.first_name || "",
      last_name: freelancer?.last_name || "",
      role: freelancer?.role || "",
      partner_tier: expert.partner_tier || null,
      social_links: {
        linkedin_url: data?.social_links?.linkedin_url || "",
        github_url: data?.social_links?.github_url || "",
        x_url: data?.social_links?.x_url || "",
        fiverr_url: data?.social_links?.fiverr_url || "",
        upwork_url: data?.social_links?.upwork_url || "",
        toptal_url: data?.social_links?.toptal_url || "",
        storetasker_url: data?.social_links?.storetasker_url || "",
        other_listing_url: data?.social_links?.other_listing_url || "",
        website: data?.social_links?.website || "",
        shopify_directory_link: data?.social_links?.shopify_directory_link || "",
        booking_call_url: data?.social_links?.booking_call_url || "",
        google_merchant: data?.social_links?.google_merchant || "",
      },
      address: {
        address: data?.address?.address || "",
        latitude: data?.address?.latitude,
        longitude: data?.address?.longitude,
      },
    };
    return expertProfile;
  };

  const api = useApi();
  const queryClient = useQueryClient();

  const hasExpertQuery = useQuery({
    queryKey: ["expert", "hasExpert"],
    queryFn: () => api.expert.hasExpertProfile(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const profileQuery = useQuery({
    enabled: () => {
      if (!hasExpertQuery.data?.value) return true;
      return !!hasExpertQuery.data.value.has_expert;
    },
    queryKey: ["expert", "profileDetails"],
    queryFn: () => api.expert.getProfileDetails(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const fetchProfile = async () => {
    profileError.value = null;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["expert", "hasExpert"] }),
      queryClient.invalidateQueries({ queryKey: ["expert", "profileDetails"] }),
    ]);
  };

  const createTriggered = ref(false);

  const createProfileMutation = useMutation({
    mutationKey: ["expert", "createProfile"],
    mutationFn: () => api.expert.createExpertProfile(),
    onSuccess: async () => {
      createTriggered.value = false;
      toaster.create({ title: "Profile created successfully", type: "success" });
      await fetchProfile();
    },
    onError: (err: Error & { response?: { data?: { message?: string } } }) => {
      createTriggered.value = false;
      toaster.create({ title: getErrorMessage(err), type: "error" });
      console.error('[useProfileDetails] createProfile error:', err);
    }
  });

  const createProfile = () => {
    if (createTriggered.value) return;
    createTriggered.value = true;
    createProfileMutation.mutate();
  };

  watch(() => hasExpertQuery.data?.value, (data) => {
    hasExpert.value = data?.has_expert ?? null;
  }, { immediate: true });

  watch(() => hasExpertQuery.error?.value, (err) => {
    if (err) {
      profileError.value = getErrorMessage(err as Error & { response?: { data?: { message?: string } } });
      toaster.create({ title: profileError.value, type: "error" });
      console.error('[useProfileDetails] hasExpert error:', err);
    }
  }, { immediate: true });

  watch(() => profileQuery.error?.value, (err) => {
    if (err) {
      profileError.value = getErrorMessage(err as Error & { response?: { data?: { message?: string } } });
      toaster.create({ title: profileError.value, type: "error" });
      console.error('[useProfileDetails] profileDetails error:', err);
    }
  }, { immediate: true });

  watch(() => profileQuery.data?.value, (data) => {
    if (!data) return;
    overallReviews.value = data.reviews ?? null;
    const mappedProfile = mapToProfile(data);
    if (mappedProfile) profile.value = mappedProfile;
    offeredServices.value = (data as { offered_services?: unknown[] })?.offered_services || null;
    badges.value = data.expert?.badges || [];
  }, { immediate: true });

  const hasActiveProSubscription = computed(() => {
    return hasExpertQuery.data?.value?.is_pro ?? false;
  });

  const subcategoryLimit = computed(() =>
    hasExpertQuery.data?.value?.subcategory_limit
      ?? profileQuery.data?.value?.expert?.subcategory_limit
      ?? (hasActiveProSubscription.value ? 15 : 3),
  );

  const subcategoryLimitIsOverride = computed(() =>
    hasExpertQuery.data?.value?.subcategory_limit_is_override
      ?? profileQuery.data?.value?.expert?.subcategory_limit_is_override
      ?? false,
  );

  const packagedServicesLimit = computed(() => profileQuery.data?.value?.expert?.packaged_services_limit ?? null);
  const faqsLimit = computed(() => profileQuery.data?.value?.expert?.faqs_limit ?? null);

  return {
    isLoadingProfile,
    profileError,
    profile,
    hasExpert,
    offeredServices,
    overallReviews,
    badges,
    hasActiveProSubscription,
    subcategoryLimit,
    subcategoryLimitIsOverride,
    packagedServicesLimit,
    faqsLimit,
    refreshProfile: fetchProfile,
    createProfile,
  };
}

export default useProfileDetails;
